import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./LandingPage.css";
import RaceTrack from "../components/RaceTrack";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../auth';
import f1Calendar2025 from '../data/f1-calendar-2025.json';

const LandingPage = () => {
  const audioRef = useRef(null);
  const calendarRef = useRef(null);
  const featuresRef = useRef(null);
  const howItWorksRef = useRef(null);
  const [races, setRaces] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Audio effect
  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      audioElement.volume = 0.3;
      const playAudio = () => {
        audioElement.play().catch((error) => {
          console.log("Autoplay blocked:", error);
        });
      };
      document.addEventListener("click", playAudio, { once: true });
      document.addEventListener("touchstart", playAudio, { once: true });
    }
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, []);

  // Fetch races
  useEffect(() => {
    const fetchRaceCalendar = async () => {
      try {
        // Try 2025 first from API
        let res = await axios.get("https://ergast.com/api/f1/2025.json");
        
        // If 2025 has no races yet, use local JSON file
        if (!res.data.MRData.RaceTable.Races || res.data.MRData.RaceTable.Races.length === 0) {
          console.log("2025 API calendar empty, using local 2025 calendar");
          setRaces(f1Calendar2025.MRData.RaceTable.Races);
          return;
        }
        
        setRaces(res.data.MRData.RaceTable.Races);
      } catch (err) {
        console.error("Error fetching races from API:", err);
        // Use local 2025 calendar as fallback
        console.log("Using local 2025 calendar");
        setRaces(f1Calendar2025.MRData.RaceTable.Races);
      }
    };
    fetchRaceCalendar();
  }, []);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Navigation Bar */}
      <nav className={`landing-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="navbar-logo">
            <span className="logo-icon">🏎️</span>
            <span className="logo-text">F1 Fantasy</span>
          </div>
          <div className="navbar-links">
            <button className="nav-link" onClick={() => scrollToSection(featuresRef)}>Features</button>
            <button className="nav-link" onClick={() => scrollToSection(howItWorksRef)}>How It Works</button>
            <button className="nav-link" onClick={() => scrollToSection(calendarRef)}>2025 Calendar</button>
            <button className="nav-link nav-link-special" onClick={() => navigate('/calendar-2026')}>2026 Calendar 🆕</button>
          </div>
          <div className="navbar-actions">
            <button className="nav-btn nav-btn-login" onClick={handleLoginClick}>Login</button>
            <button className="nav-btn nav-btn-signup" onClick={handleSignupClick}>Sign Up</button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video */}
      <div className="landing-hero">
        <audio ref={audioRef} src="/audio/race-start.mp3" preload="auto" />
        <video className="video-bg" autoPlay loop muted playsInline>
          <source src="/videos/f1-race.mp4" type="video/mp4" />
        </video>
        <div className="video-overlay"></div>

        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-icon">🏆</span>
            <span>2025 Season Now Open</span>
          </div>
          
          <h1 className="hero-title">
            Become The <span className="gradient-text">Team Principal</span>
            <br />
            Of Your Own F1 Team
          </h1>
          
          <p className="hero-subtitle">
            Build your dream team. Make strategic predictions. Compete with friends.
            <br />
            Experience Formula 1 like never before.
          </p>

          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">100M</div>
              <div className="stat-label">Budget</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">20+</div>
              <div className="stat-label">Drivers</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">10</div>
              <div className="stat-label">Teams</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">24</div>
              <div className="stat-label">Races</div>
            </div>
          </div>

          <div className="hero-cta">
            <button className="cta-btn cta-primary" onClick={handleGetStarted}>
              <span>Start Your Journey</span>
              <span className="btn-icon">→</span>
            </button>
            <button className="cta-btn cta-secondary" onClick={() => scrollToSection(howItWorksRef)}>
              <span>Learn More</span>
            </button>
          </div>

          <div className="hero-scroll-indicator" onClick={() => scrollToSection(featuresRef)}>
            <div className="scroll-mouse">
              <div className="scroll-wheel"></div>
            </div>
            <span>Scroll to explore</span>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section ref={featuresRef} className="features-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Why Choose Us</span>
            <h2 className="section-title">Everything You Need To Win</h2>
            <p className="section-subtitle">
              Powerful features designed for the ultimate F1 fantasy experience
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🏎️</div>
              <h3 className="feature-title">Build Your Team</h3>
              <p className="feature-description">
                Pick 5 drivers and 2 constructors within your £100M budget. Make strategic choices to maximize points.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3 className="feature-title">Race Predictions</h3>
              <p className="feature-description">
                Predict podium finishes, fastest laps, and more. Score points for accurate predictions before each race.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🏆</div>
              <h3 className="feature-title">Private Leagues</h3>
              <p className="feature-description">
                Create or join leagues with friends. Compete for bragging rights and track your ranking all season long.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3 className="feature-title">Live Standings</h3>
              <p className="feature-description">
                Real-time leaderboards updated after every race. See how you stack up against the competition.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3 className="feature-title">Dynamic Pricing</h3>
              <p className="feature-description">
                Driver values change based on performance. Time the market right to build the ultimate team.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3 className="feature-title">Mobile Friendly</h3>
              <p className="feature-description">
                Manage your team on the go. Make last-minute changes before race start from any device.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">Simple & Fun</span>
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Get started in 3 easy steps
            </p>
          </div>

          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="step-title">Create Your Account</h3>
                <p className="step-description">
                  Sign up with email or Google. Join existing leagues or create your own private league.
                </p>
              </div>
              <div className="step-visual">👤</div>
            </div>

            <div className="step-arrow">→</div>

            <div className="step-card">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="step-title">Build Your Team</h3>
                <p className="step-description">
                  Select 5 drivers and 2 constructors within your £100M budget. Choose wisely!
                </p>
              </div>
              <div className="step-visual">🏗️</div>
            </div>

            <div className="step-arrow">→</div>

            <div className="step-card">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="step-title">Predict & Win</h3>
                <p className="step-description">
                  Make predictions before each race. Earn points and climb the leaderboard!
                </p>
              </div>
              <div className="step-visual">🎯</div>
            </div>
          </div>

          <div className="cta-container">
            <button className="cta-btn cta-primary" onClick={handleGetStarted}>
              <span>Start Playing Now</span>
              <span className="btn-icon">🏁</span>
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="section-container">
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-box-number">100%</div>
              <div className="stat-box-label">Free to Play</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-number">24</div>
              <div className="stat-box-label">Races Per Season</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-number">∞</div>
              <div className="stat-box-label">Leagues to Join</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-number">Real-time</div>
              <div className="stat-box-label">Updates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Race Calendar Section */}
      <section ref={calendarRef} className="calendar-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">2025 Season</span>
            <h2 className="section-title">Race Calendar</h2>
            <p className="section-subtitle">
              24 races across 5 continents. Don't miss a single Grand Prix.
            </p>
          </div>
          <RaceTrack races={races} />
          
          <div className="calendar-year-switch">
            <button className="year-switch-btn" onClick={() => navigate('/calendar-2026')}>
              <span className="year-icon">📅</span>
              <div className="year-text">
                <span className="year-title">View 2026 Calendar</span>
                <span className="year-subtitle">See next season's schedule</span>
              </div>
              <span className="year-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="section-container">
          <div className="final-cta-content">
            <h2 className="final-cta-title">
              Ready to Dominate the 2025 Season?
            </h2>
            <p className="final-cta-subtitle">
              Join thousands of F1 fans competing for glory. Build your team today.
            </p>
            <button className="cta-btn cta-primary cta-large" onClick={handleGetStarted}>
              <span>Create Free Account</span>
              <span className="btn-icon">🏆</span>
            </button>
            <p className="final-cta-note">No credit card required • 100% free forever</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <span className="logo-icon">🏎️</span>
                <span className="logo-text">F1 Fantasy</span>
              </div>
              <p className="footer-tagline">
                Your ultimate Formula 1 fantasy league experience.
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#how-it-works">How It Works</a>
                <a href="#calendar">Calendar</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#about">About</a>
                <a href="#contact">Contact</a>
                <a href="#support">Support</a>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <a href="#privacy">Privacy Policy</a>
                <a href="#terms">Terms of Service</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 F1 Fantasy League. All rights reserved.</p>
            <p>Not affiliated with Formula 1®</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
